import type { FC } from 'react';
import * as React from 'react';
import { Card, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';

interface ReceiptBatchOverviewProps {
  receiptsCount: number;
  totalAmount: number;
  ReceiptBatchImageInput: React.ReactElement;
  autoGenerateReceipts?: boolean;
}

export const ReceiptBatchOverview: FC<ReceiptBatchOverviewProps> = ({
  receiptsCount,
  totalAmount,
  ReceiptBatchImageInput,
  autoGenerateReceipts,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const needMoreSpace = autoGenerateReceipts;
  return (
    <Grid item xs={12} md={6} lg={needMoreSpace ? 3.5 : 3.1}>
      <Card
        sx={{
          p: 2,
          height: '100%',
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <OverviewItem
            label={t('common.receipts_plural')}
            unit="#"
            value={receiptsCount.toString()}
          />
          <OverviewItem
            align={'right'}
            label={t('common.total-amount')}
            unit="€"
            value={fCurrencyCents(totalAmount < 10000000 ? totalAmount : 0)
              .replace('€', '')
              .trim()}
          />
        </Stack>
        {ReceiptBatchImageInput}
      </Card>
    </Grid>
  );
};

interface OverviewItemProps {
  label: string;
  unit: string; // can be # or €
  value: string;
  align?: 'left' | 'right';
}

const OverviewItem: FC<OverviewItemProps> = ({ label, unit, value, align }) => {
  return (
    <Stack>
      <Typography
        variant="caption"
        color="grey.500"
        fontWeight={700}
        sx={{
          textTransform: 'uppercase',
          textAlign: align,
        }}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
      >
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            mb: 0.7,
            mr: 0.5,
          }}
        >
          {unit}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ textAlign: align }}>
          {value}
        </Typography>
      </Stack>
    </Stack>
  );
};
